import { OnInit, OnDestroy, AfterViewInit, Input, ChangeDetectorRef, Directive } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { FormGroup, FormControl, FormBuilder, FormArray } from '@angular/forms';

import { Subscription } from 'rxjs';

import { ConfirmDialogService } from '../services';

@Directive()
export abstract class ArraySectionEditComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {
  entityForm: FormArray = this.createEntityForm();
  removeConfirmationMessage = 'Are you sure you want to delete this item?';

  abstract validationMessages: any;

  private entityFormChanges$: Subscription;
  private serverError = null;
  private _isDisabled = false;

  constructor(
    protected fb: FormBuilder,
    protected cdr: ChangeDetectorRef,
    protected dialogService: ConfirmDialogService,
  ) {}

  ngOnInit() {}

  ngOnDestroy() {
    this.stopWatchingFormChanges();
  }

  ngAfterViewInit() {
    this.startWatchingFormChanges();
  }

  abstract addItem(item?: any);

  clickAddItem(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.addItem();
    this.cdr.markForCheck();
  }

  clickRemoveItem(event: Event, formControl: FormGroup): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialogService.confirm(this.removeConfirmationMessage).then((result) => {
      if (result) {
        const index = this.entityForm.controls.indexOf(formControl);
        this.entityForm.removeAt(index);
        this.cdr.markForCheck();
      }
    });
  }

  valueSet = (_: any) => {};

  propagateChange = (_: any) => {};

  writeValue(value: any) {
    this.stopWatchingFormChanges();

    this.entityForm = this.createEntityForm();

    if (value) {
      for (const item of value) {
        this.addItem(item);
      }
      this.entityForm.patchValue(value, { onlySelf: true });
      this.cdr.markForCheck();
    }

    if (this._isDisabled) {
      this.entityForm.disable();
    }

    this.valueSet(value);

    this.startWatchingFormChanges();
  }

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}

  setDisabledState?(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
    if (isDisabled) {
      this.entityForm.disable({ emitEvent: false });
    } else {
      this.entityForm.enable({ emitEvent: false });
    }
  }

  validate(control: FormControl) {
    if (control.errors) {
      for (const fieldError of Object.keys(control.errors)) {
        const controlNames = fieldError
          .split(/\W/)
          .filter((v) => v !== '')
          .map((v) => this.normalizeName(v));

        for (let index = controlNames.length; index > 0; index--) {
          const controlPath = controlNames.slice(0, index).join('.');
          const nestedControl = this.entityForm.get(controlPath);
          if (nestedControl) {
            let newControlPath = controlNames.slice(index).join('.');
            if (newControlPath === '') {
              newControlPath = 'formError';
            }
            nestedControl.setErrors({
              [newControlPath]: control.errors[fieldError],
            });
            nestedControl.updateValueAndValidity({ emitEvent: false, onlySelf: true });
            break;
          }
        }
      }
    }

    if (!this.serverError && control.errors && control.errors.formError) {
      this.serverError = control.errors.formError;
      this.entityForm.setErrors({ ...this.entityForm.errors, serverError: this.serverError });
      this.cdr.markForCheck();
      return { serverError: this.serverError };
    } else {
      this.serverError = null;
    }

    return this.entityForm.invalid ? this.entityForm.errors || { error: true } : null;
  }

  //  Drag and drop functions
  drop(event: { previousIndex; currentIndex }) {
    this.moveItemInFormArray(this.entityForm, event.previousIndex, event.currentIndex);
  }

  moveItemInFormArray(array: FormArray, fromIndex: number, toIndex: number): void {
    const from = this.clamp(fromIndex, array.length - 1);
    const to = this.clamp(toIndex, array.length - 1);

    if (from === to) {
      return;
    }

    const target = array.at(from);
    const delta = to < from ? -1 : 1;

    for (let i = from; i !== to; i += delta) {
      array.setControl(i, array.at(i + delta));
    }

    array.setControl(to, target);
  }

  clamp(value: number, max: number): number {
    return Math.max(0, Math.min(max, value));
  }

  protected createEntityForm(): FormArray {
    return this.fb.array([]);
  }

  private stopWatchingFormChanges() {
    if (this.entityFormChanges$) {
      this.entityFormChanges$.unsubscribe();
    }
  }

  private startWatchingFormChanges() {
    this.entityFormChanges$ = this.entityForm.valueChanges.subscribe((m) => {
      if (this._isDisabled === false) {
        this.propagateChange(m);
      }
    });
  }

  private normalizeName(str: string): string {
    return str.replace(/^\w/g, (match: string): string => {
      return match.toLowerCase();
    });
  }
}
